import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { useRequestHandler } from '@/hooks/useRequestHandler'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { createMeetingLink, selectMeetingLinkCreateRequestTracker, selectNewlyCreatedMeetingLink } from '@/redux/reducers/meeting-link.reducer'
import { getSpecialityOptions, selectSpecialityOptions } from '@/redux/reducers/speciality-option.reducer'
import { getStaffs, selectStaffs } from '@/redux/reducers/staff.reducer'

import { CreateMeetingLinkWizard, CreateMeetingLinkWizardValues } from '../components/CreateMeetingLinkWizard'
import SpecialitiesAutoCompleteContainer from '@/components/formik/fields/SpecialitiesAutocompleteField'
import { isStaff } from '@/utils/type-guards'

export default function CreateMeetingLinkFormContainer() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const authUser = useAppSelector(selectAuthUser)
  const lastestCreatedMeetingLink = useAppSelector(selectNewlyCreatedMeetingLink)
  const specialityOptions = useAppSelector(selectSpecialityOptions)
  const staffs = useAppSelector(selectStaffs)

  const createMeetingLinkRequestTracker = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.meetingLink.create.success'), { variant: 'success' })
      if (lastestCreatedMeetingLink) {
        navigate(`/meeting-links/${lastestCreatedMeetingLink._id}?tab=availabilities`)
      }
    },
    onError: () => {
      enqueueSnackbar(t('notification.meetingLink.create.error'), { variant: 'error' })
    },
    requestTrackerSelector: selectMeetingLinkCreateRequestTracker,
  })

  const staffsData = useMemo(
    () =>
      staffs.map((staff) => ({
        id: staff._id,
        cps: staff.cps || '',
        name: staff.userInfo.name,
        surname: staff.userInfo.surname,
        status: staff.status,
        isSameEtablissement: staff.establishment === authUser?.establishment?._id,
      })),
    [staffs, authUser]
  )

  useEffect(() => {
    dispatch(getSpecialityOptions())
    const query = {
      name: '',
      surname: '',
      status: { $in: ['MEDECIN', 'PDS'] }
    }
    const options = {
      limit: 0,
      skip: 0,
      orderBy: '',
    }
    dispatch(getStaffs(query, options))
  }, [dispatch])

  const handleSubmit = (values: CreateMeetingLinkWizardValues) => {
    if (!authUser) return

    createMeetingLinkRequestTracker.dispatch(createMeetingLink({
      ...values,
      staffs: [...values.staffs, authUser._id],
      specialities: values.specialities.map(spec => {
        return {
          code: spec.code,
          name: spec.name,
          price: spec.price
        }
      }),
      waitingRoom: values.waitingRoom || undefined
    }))
  }

  return (
    <CreateMeetingLinkWizard
      disableStaffSelection={isStaff(authUser) && authUser?.status === 'MEDECIN'}
      onSubmit={handleSubmit}
      initialValues={{
        busyEnabled: true,
        description: '',
        name: '',
        specialities: [],
        staffs: [],
        immediateConsultationConfig: {
          enabled: false,
          maxWaitingPatients: null,
          requirePaymentUpfront: true,
          price: null,
          paymentReceiver: null,
        },
        waitingRoom: null,
      }}
      isSubmitting={false}
      staffs={staffsData}
      SpecialitiesSelection={<SpecialitiesAutoCompleteContainer id="speciality" name={'specialities'} editable={true} label={t('field.speciality.label', { context: 'plural' })} />}
    />
  )
}
